import React, { useEffect, useState } from 'react';
import {ReactComponent as Icon } from '../../assets/icons/Edit.svg'
import {useDisclosure } from '@mantine/hooks';
import TableMedusa from '../../components/Table';
import {CategoryRowType, CategoryDetailRowType} from "../../types/categories";
import { Table, Button, NumberInput, Select } from '@mantine/core';
import {ReactComponent as RemoveIcon} from '../../assets/icons/Remove.svg'
import PopUp from '../../components/PopUp';
import api from '../../api/axiosClient';

interface TextileTemplateOption {
    label: string;
    value: string;
}

interface TextileTypeOptionResponse {
    id: number;
    name: string;
    code?: string;
}

interface MaterialsListProps {
    details: CategoryDetailRowType[];
    onChange?: (updatedDetails: CategoryDetailRowType[]) => void;
}

const MaterialsList = ({details, onChange}: MaterialsListProps) => {
    const MAX_MATERIAL_ROWS = 10;
    const icon = <Icon width={19} height={19}></Icon>;
    const removeIcon = <RemoveIcon width={19} height={19}></RemoveIcon>;
    const [deleteDetailId, setDeleteDetailId] = useState<number | null>(null);
    const [openedDel, handlerDel] = useDisclosure(false);
    const [textileOptions, setTextileOptions] = useState<TextileTemplateOption[]>([]);

    useEffect(() => {
        const fetchTextileTypes = async () => {
            try {
                const response = await api.get<TextileTypeOptionResponse[]>(`/textil-types/options`);
                const normalizedOptions = response.data.map((item) => ({
                    label: item.code || item.name,
                    value: String(item.id)
                }));
                setTextileOptions(normalizedOptions);
            } catch (error) {
                console.error("Error fetching textile types in list:", error);
                setTextileOptions([]);
            }
        };
        fetchTextileTypes();
    }, []);

    let table = null;

    const normalizeNullableNumber = (val: string | number | null | undefined) => {
        if (val === '' || val === undefined || val === null) {
            return null;
        }

        return Number(val);
    };

    const handleFieldChange = (id: number, field: keyof CategoryDetailRowType, val: any) => {
        const updatedDetails = details.map((item) => {
            if (item.id === id) {
                return { ...item, [field]: val };
            }
            return item;
        });
        if (onChange) {
            onChange(updatedDetails);
        }
    };

    const onClickRemove = (id:number)=>{
        setDeleteDetailId(id);
        handlerDel.open();
    }

    const onClickDelete = () => {
        if (deleteDetailId !== null) {
            const updatedDetails = details.filter((item) => item.id !== deleteDetailId);
            if (onChange) {
                onChange(updatedDetails);
            }
        }
        setDeleteDetailId(null);
        handlerDel.close();
    }

    const onClickAddRow = () => {
        if (details.length >= MAX_MATERIAL_ROWS) {
            return;
        }

        // Create a new detail row with negative/dummy ID or custom tracking ID if the list is flat
        // Let's generate a temporary unique negative ID so it doesn't collide with existing IDs
        const minId = details.reduce((min, item) => (item.id < min ? item.id : min), 0);
        const newId = minId <= 0 ? minId - 1 : -1;

        const newDetail: CategoryDetailRowType = {
            id: newId,
            category_id: 0, // Will be linked on save on backend as necessary
            textil_type_id: textileOptions.length > 0 ? Number(textileOptions[0].value) : 0,
            conv_value: 0,
            max_value: 100,
            min_value: 0,
        };

        if (onChange) {
            onChange([...details, newDetail]);
        }
    };

    const sortedDetails = [...details].sort(
            (a, b) => (a.id ?? 0) - (b.id ?? 0)
    );  

    const rows = sortedDetails.map(({ id, conv_value, max_value, min_value, textil_type_id }) => (
        <Table.Tr key={id}>
            <Table.Td>{id < 0 ? '' : id}</Table.Td>
            <Table.Td>
                <Select
                    placeholder="Select textile"
                    data={textileOptions}
                    value={String(textil_type_id)}
                    onChange={(val) => handleFieldChange(id, 'textil_type_id', Number(val))}
                    searchable
                />
            </Table.Td>
            <Table.Td>
                <NumberInput
                    placeholder="Conv"
                    value={conv_value}
                    onChange={(val) => handleFieldChange(id, 'conv_value', Number(val ?? 0))}
                />
            </Table.Td>
            <Table.Td>
                <NumberInput
                    placeholder="Max"
                    value={max_value ?? undefined}
                    onChange={(val) => handleFieldChange(id, 'max_value', normalizeNullableNumber(val))}
                />
            </Table.Td>
            <Table.Td>
                <NumberInput
                    placeholder="Min"
                    value={min_value ?? undefined}
                    onChange={(val) => handleFieldChange(id, 'min_value', normalizeNullableNumber(val))}
                />
            </Table.Td>
            <Table.Td>
                <Button variant="transparent" onClick={()=>onClickRemove(id)}>{removeIcon}</Button>
            </Table.Td>
        </Table.Tr>
    ));
    table = <TableMedusa headers={["ID", "Textile Type", "Conversion Value", "Max Value", "Min Value", "Actions"]} rows={rows}></TableMedusa>

    return(
        <div>
            <PopUp opened={openedDel} close={handlerDel.close} size={700} title="Warning" onSave={onClickDelete} button1="Delete" >Are you sure you want to delete this detail?</PopUp>
            {table}
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-start' }}>
                <Button variant="outline" color="#474973" radius="xl" onClick={onClickAddRow} disabled={details.length >= MAX_MATERIAL_ROWS}>
                    {details.length >= MAX_MATERIAL_ROWS ? 'Max 10 rows reached' : 'Add New Row'}
                </Button>
            </div>
        </div>
    )
}
export default MaterialsList;